import React, {useEffect, useState} from "react";
import {Alert, Button, Card, Col, Form, Row, Table} from "react-bootstrap";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import VenuesService from "../../services/venues.service";
import {
    GENRES,
    isLoggedIn,
    isLoggedInWithRole,
    isLoggedInWithRoleAndUserIs,
    SLOT_DESCRIPTIONS,
    slotActIdMatch,
    toStrArray,
    VENUE_ELECTRIC,
    VENUE_SIZES,
    VENUE_SOUND
} from "../../common/utils";
import {useForm} from "react-hook-form";
import AuthService from "../../services/auth.service";
import {API_EDITS_ALLOWED} from "../../services/axios.utils";
import {Link} from "react-router-dom";

const VenueEdit = ({venue}) => {
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm()

    let canEdit = isLoggedInWithRoleAndUserIs('host', venue.creator_id);
    let canEditNew = isLoggedInWithRole('host');
    let allowEdit = canEdit || (canEditNew && (venue.id === undefined));

    let user = AuthService.getCurrentUser();
    let loggedIn = isLoggedIn();
    let editsAllowed = API_EDITS_ALLOWED === "true" || (loggedIn && user.is_admin);

    // object that represents a map of slot_def to availability (true/false)
    const [slotAvailabilities, setSlotAvailabilities] = useState({});

    const reactQuillRef = React.useRef();

    // limit to 1500 characters
    const checkCharacterCount = (event) => {
        const unprivilegedEditor = reactQuillRef.current.unprivilegedEditor;
        if (unprivilegedEditor.getLength() > 1500 && event.key !== 'Backspace') {
            // event.preventDefault();
            window.alert("You've reached the maximum character count of 1500.")
        }
    };

    const initializeSlotInfo = () => {
        for (let i = 1; i < 5; i++) {
            let sd = "Slot " + i
            slotAvailabilities[sd] = venue.available_slots.some(element => [sd].includes(element.slot_def));
        }
        console.log("init: slotAvailabilities=" + JSON.stringify(slotAvailabilities));
    }

    const fetchVenue = () => {
        VenuesService.getVenueById(venue.id).then(
            response => {
                // console.log("venue:" + JSON.stringify(response.data));
                initializeSlotInfo();
                let mi = {}
                for (let i = 1; i < 5; i++) {
                    mi["Slot " + i] = slotActIdMatch(response.data.available_slots, "Slot " + i);
                }
                venue = response.data;
            },
            error => {
                console.log(error);
            });
    }

    useEffect(() => {
        if (venue.id !== undefined) {
            fetchVenue();
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    // cannot register venueBio like the other fields because the input type is react-quill
    useEffect(() => {
        register("venueBio", {required: true});
        setValue("venueBio", venue.bio);
    }, [register]);  // eslint-disable-line react-hooks/exhaustive-deps

    const updateSlotAvailability = (slotDef, isAvailable) => {
        // console.log("!!!FIRED!!! - slotDef=" + slotDef + ", isAvailable=" + isAvailable)
        let newAvailability = {};
        for (let i = 1; i < 5; i++) {
            let sd = "Slot " + i;
            if (sd === slotDef) {
                newAvailability[sd] = isAvailable;
            } else {
                newAvailability[sd] = slotAvailabilities[sd];
            }
        }
        console.log("newAvailability=" + JSON.stringify(newAvailability));
        setSlotAvailabilities(newAvailability);
    }

    const onEditorStateChange = (editorState) => {
        // disabling matchVisual did not work: https://stackoverflow.com/questions/43748108/how-to-keep-quill-from-inserting-blank-paragraphs-pbr-p-before-heading
        if (editorState === "<p><br></p>") {
            editorState = ""
        }
        setValue("venueBio", editorState);
    };

    const handleCancel = (event) => {
        event.preventDefault();
        window.location.assign("/venues");
    }

    // todo: handle backend errors (non-200 or 204)
    // todo: validate address is unique (integrity error)
    const handleSubmitRHF = (data) => {
        console.log("=== data: " + JSON.stringify(data));

        // populate the slots part of the payload
        console.log("on submit - slotAvailabilities=" + JSON.stringify(slotAvailabilities));

        let slots = []
        // for a new venue
        if (venue.id === undefined) {
            data.venueSlotsAvail.forEach(s => {
                slots.push({"slot_def": s});
            })
        } else {
            for (let index = 1; index < 5; index++) {
                let sd = "Slot " + index;
                if (slotAvailabilities[sd]) {
                    slots.push({"slot_def": sd});
                }
            }
        }

        let payload = {
            address: data.venueAddress,
            bio: data.venueBio,
            rain_date_available: data.venueRainDateAvail,
            preferred_act_types: toStrArray(data.venuePrefGenres),
            available_slots: slots,
            size_pref: data.venueSizePref,
            provide_electric: data.venueProvideElectric,
            provide_sound_system: data.venueProvideSoundSystem,
        }
        console.log("payload: " + JSON.stringify(payload))

        if (venue.id === undefined) {
            VenuesService.createVenue(payload).then(
                response => {
                    console.log("venue:" + JSON.stringify(response.data));
                    // todo: pass redirect url since could be coming from "/myvenues"
                    window.location.assign("/venues");
                },
                error => {
                    console.log(error);
                });

        } else {
            VenuesService.updateVenueNoSlots(venue.id, payload).then(
                response => {
                    console.log("venue:" + JSON.stringify(response.data));
                    // todo: pass redirect url since could be coming from "/myvenues"
                    window.location.assign("/venues");
                },
                error => {
                    console.log(error);
                });
        }
    }

    return (
        <>
            {!allowEdit &&
                <div>You are not allowed to perform this action.</div>
            }
            {allowEdit &&
                <div>
                    {!editsAllowed &&
                        <Alert variant="danger">
                            Registration is closed for this year's Porchfest. If you need to modify your venue,
                            please <Link to="/contact" reloadDocument="true">contact us</Link>.
                        </Alert>
                    }

                    {/*"handleSubmit" will validate your inputs before invoking "onSubmit"*/}
                    <Form onSubmit={handleSubmit(handleSubmitRHF)}>

                        <Row className="mb-3">
                            <Col>
                                <div>Please fill out the form below:</div>
                                <div className="text-danger">
                                    <span className="fs-5 text">*</span> Indicates a required question
                                </div>
                            </Col>
                        </Row>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What is your street address?</strong>&nbsp;<span
                                    className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Control
                                        type="text"
                                        defaultValue={venue.address}
                                        placeholder="Enter the street address of your venue (ex: 100 Main Street)"
                                        {...register("venueAddress", {required: true})}
                                    />
                                    {errors.venueAddress &&
                                        <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>How do you describe your venue?</strong>&nbsp;<span
                                    className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    Use the area below to provide:
                                    <ul>
                                        <li>A brief description of your venue</li>
                                        <li>Anything you want performers to know (layout, amenities, etc.)</li>
                                    </ul>
                                </div>
                                <div>
                                    <ReactQuill theme="snow"
                                                defaultValue={venue.bio}
                                                onChange={onEditorStateChange}
                                                modules={{
                                                    toolbar: [
                                                        // [{ 'header': [1, 2, false] }],
                                                        ['bold', 'italic', 'underline'],
                                                        [{'list': 'ordered'}, {'list': 'bullet'}],
                                                        ['link'],
                                                        ['clean']
                                                    ],
                                                    // this is needed to eliminate the <p><br></p> problem
                                                    clipboard: {
                                                        matchVisual: false
                                                    }
                                                }}
                                        // placeholder={"Describe your venue, any amenities you can provide to performers, etc..."}
                                                onKeyDown={checkCharacterCount}
                                                ref={reactQuillRef}
                                    />
                                    {errors.venueBio &&
                                        <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What is your preferred act size?</strong>&nbsp;<span
                                    className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={venue.size_pref}
                                        {...register("venueSizePref", {required: true})}>
                                        {VENUE_SIZES.map((s, index) => (
                                            <option key={index} value={s.value}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.venueSizePref &&
                                        <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What are your preferred act genres?</strong>&nbsp;<span
                                    className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    {GENRES.map((g, index) => {
                                        return (
                                            <Form.Check
                                                key={index}
                                                label={g.name}
                                                defaultChecked={venue.preferred_act_types.includes(g.value)}
                                                {...register("venuePrefGenres", {required: true})}
                                                value={g.value}
                                                className="float-start pf-float-divs"
                                            />
                                        )
                                    })}
                                </div>
                                {errors.venuePrefGenres &&
                                    <span className="pf-validation-error">At least one genre is required</span>}
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>Can you provide electricity for performers to use?</strong>&nbsp;<span
                                    className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={venue.provide_electric}
                                        {...register("venueProvideElectric", {required: true})}>
                                        {VENUE_ELECTRIC.map((ve, index) => (
                                            <option key={index} value={ve.value}>{ve.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.venueProvideElectric &&
                                        <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>Do you have a sound system that you can provide for the day (not required
                                        for hosting)?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={venue.provide_sound_system}
                                        {...register("venueProvideSoundSystem", {required: true})}>
                                        {VENUE_SOUND.map((vs, index) => (
                                            <option key={index} value={vs.value}>{vs.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.venueProvideSoundSystem &&
                                        <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>If we need a rain date (June 15), are you available to host that
                                        day?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <div>
                                        <Form.Check
                                            type="radio"
                                            label="Available"
                                            defaultChecked={venue.rain_date_available}
                                            {...register("venueRainDateAvail", {required: true})}
                                            value="true"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            type="radio"
                                            label="Unavailable"
                                            defaultChecked={!venue.rain_date_available}
                                            {...register("venueRainDateAvail", {required: true})}
                                            value="false"
                                        />
                                    </div>
                                </div>
                                {errors.venueRainDateAvail &&
                                    <span className="pf-validation-error">This field is required</span>}
                            </Card.Body>
                        </Card>

                        {/* for a new venue only */}
                        {venue.id === undefined &&
                            <Card>
                                <Card.Body>
                                    <div className="mb-3">
                                        <strong>What time slots would you like to host?</strong>&nbsp;<span
                                        className="fs-5 text text-danger">*</span>
                                    </div>
                                    <div>
                                        <Table bordered hover size="lg" responsive>
                                            {/*<thead>*/}
                                            {/*<tr>*/}
                                            {/*    <th>Availability</th>*/}
                                            {/*</tr>*/}
                                            {/*</thead>*/}

                                            <tbody>
                                            {Array.from({length: Object.keys(SLOT_DESCRIPTIONS).length}).map((_, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Form.Check
                                                            label={"Slot " + (index + 1) + ": " + (SLOT_DESCRIPTIONS["Slot " + (index + 1)]) + (slotAvailabilities["Slot " + (index + 1)] ? " is available" : " is unavailable")}
                                                            {...register("venueSlotsAvail", {required: true})}
                                                            value={"Slot " + (index + 1)}
                                                            onChange={(e) => {
                                                                updateSlotAvailability("Slot " + (index + 1), e.target.checked)
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                        {errors.venueSlotsAvail &&
                                            <span className="pf-validation-error">At least one slot is required</span>}
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        <Row className="mb-3">
                            <Col sm={12}>
                                <Button type="submit" disabled={!editsAllowed}>Save</Button>
                                &nbsp;&nbsp;
                                {/*<Link to="/venues">Cancel</Link>*/}
                                <Button type="reset" variant="outline-secondary" onClick={handleCancel}>Cancel</Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            }
        </>
    )
};

export default VenueEdit;
